import React from 'react';
import { InboxOutlined } from '@ant-design/icons';
import { Upload, Button } from 'antd';
import { UploadFile } from 'antd/es/upload/interface';
import { useNotificationContext } from '@/features/notifications';
import { useFileUpload } from '@/shared/hooks';
import {
  ALLOWED_FILE_EXTENSIONS,
  FILE_SIZE_LIMIT_IN_MEGABYTES,
} from '../config';
import { fileSchema } from '../validators';
import { FileUploadHint } from './FileUploadHint';

const { Dragger } = Upload;

export enum FileSource {
  LOCAL = 'local',
  GOOGLE_DRIVE = 'google_drive',
  CONFLUENCE = 'confluence',
  EXTRACTION = 'EXTRACTION',
  LEGACY_MEDIA = 'legacy_media',
}

type LocalFileUploadProps = {
  fileUploading: boolean;
  onUpload: (file: UploadFile, source: FileSource) => Promise<void>;
};

export const LocalFileUpload: React.FC<LocalFileUploadProps> = ({
  fileUploading,
  onUpload,
}) => {
  const { showNotification } = useNotificationContext();
  const { fileList, onBeforeUpload, onAfterUpload, onRemove } = useFileUpload(
    fileSchema,
    (message, file) => {
      showNotification({
        type: 'error',
        message: `Error uploading file "${file.name}"`,
        description: message,
      });
    },
  );

  const handleUpload = async () => {
    try {
      for (const file of fileList) {
        await onUpload(file, FileSource.LOCAL);
      }
    } catch {
      showNotification({
        type: 'warning',
        message: 'Upload error',
        description: 'Failed to upload file',
      });
    } finally {
      onAfterUpload();
    }
  };

  return (
    <div>
      <Dragger
        name="file"
        fileList={fileList}
        onRemove={onRemove}
        beforeUpload={onBeforeUpload}
        disabled={fileUploading}
        multiple
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          Drag and drop files here or click to select files
        </p>
        <p className="ant-upload-hint">Support for a single or bulk upload.</p>
        <FileUploadHint
          maxFileSizeInMb={FILE_SIZE_LIMIT_IN_MEGABYTES}
          supportedFileTypes={ALLOWED_FILE_EXTENSIONS}
        />
      </Dragger>
      <Button
        type="primary"
        onClick={handleUpload}
        disabled={!fileList.length || fileUploading}
        loading={fileUploading}
        style={{ marginTop: 16 }}
      >
        Upload
      </Button>
    </div>
  );
};
